<template>
  <div
    class="user-input"
    @click="checkPermission">
    <ValidationObserver
      ref="customFieldObserver" >
      <textarea
        v-if="fieldp.type === 'text'"
        v-model="modelValue"
        v-autoheight
        :disabled="!hasPermissionToEdit"
        placeholder="Add"
        :name="`add-${fieldp.name.replace(' ', '-')}`"
        class="edit-field" />
      <div
        v-if="fieldp.type === 'number'"
        class="number-input flex center-all">
        <input
          v-if="fieldp.type === 'number'"
          v-model="modelValue"
          placeholder="0"
          :disabled="!hasPermissionToEdit"
          class="edit-field greedy"
          type="number" >
        <SBIcon
          :icon="SBIconList.MINUS"
          :color="SBColors.blackUltralight"
          :size="8"
          :hover-color="SBColors.greyChatBubble"
          :hover-size="16"
          :hover-icon-color="SBColors.blackPrimary"
          class="ml-2"
          @click="decreaseNumber()" />
        <SBIcon
          :icon="SBIconList.PLUS"
          :color="SBColors.blackUltralight"
          :size="8"
          :hover-color="SBColors.greyChatBubble"
          :hover-size="16"
          :hover-icon-color="SBColors.blackPrimary"
          @click="increaseNumber()" />
      </div>

      <ValidationProvider
        name="email"
        rules="email">
        <input
          v-if="fieldp.type === 'email'"
          v-model="modelValue"
          type="email"
          :disabled="!hasPermissionToEdit"
          data-vv-as="Email"
          data-vv-name="email"
          data-vv-validate-on="input"
          placeholder="Add"
          class="edit-field" >
      </ValidationProvider>

      <template v-if="fieldp.type === 'date'" >
        <SBIcon
          v-if="modelValue"
          class="clear-date pointer"
          :icon="SBIconList.CLOSE_THICK"
          :color="SBColors.blackUltralight"
          :size="7"
          :hover-color="SBColors.greyChatBubble"
          :hover-size="16"
          :hover-icon-color="SBColors.blackPrimary"
          @click="deleteDate" />
        <el-date-picker
          v-model="modelValue"
          placeholder="Add"
          type="date"
          :disabled="!hasPermissionToEdit"
          align="left"
          :clearable="false"
          unlink-panels
          :format="dateFormat" />
      </template>

      <!-- TODO - during a rewrite make a radio option too even though we do not need it at the moment. -->
      <!-- <b-radio-group
        v-if="fieldp.type === 'boolean'"
        v-model="modelValue"
        :options="[{text: 'Yes', value: true}, {text: 'No', value: false}]"
        @change="radioChange"
        @click.native="radioChange(null, $event)" /> -->

      <multiselect
        v-if="fieldp.type === 'boolean'"
        v-model="modelValue"
        class="no-shadow flex-options hide-tags no-selected-background"
        :options="booleanOptions"
        :option-height="30"
        :show-labels="false"
        :allow-empty="false"
        open-direction="bottom"
        placeholder="Select"
        :disabled="!hasPermissionToEdit"
        label="text"
        track-by="value" >
        <template
          slot="selection"
          slot-scope="{ values }">
          <span
            v-if="values.length"
            class="multiselect__single" >{{ formatPermissionValues(values) }}</span>
        </template>
        <template
          slot="option"
          slot-scope="props">
          <SBIcon
            :icon="SBIconList.DONE_TICK"
            :color="SBColors.bluePrimary"
            :size="9"/>
          {{ props.option.text }}
        </template>
      </multiselect>

      <multiselect
        v-if="fieldp.type === 'list'"
        v-model="modelValue"
        class="no-shadow flex-options hide-tags no-selected-background"
        :options="options"
        :option-height="30"
        :show-labels="false"
        :allow-empty="false"
        :disabled="!hasPermissionToEdit"
        open-direction="bottom"
        placeholder="Select"
        label="name"
        track-by="value" >
        <template
          slot="selection"
          slot-scope="{ values }">
          <span
            v-if="values.length"
            class="multiselect__single" >{{ formatPermissionValues(values) }}</span>
        </template>
        <template
          slot="option"
          slot-scope="props">
          <SBIcon
            :icon="SBIconList.DONE_TICK"
            :color="SBColors.bluePrimary"
            :size="9"/>
          {{ props.option.name }}
        </template>
      </multiselect>

      <!-- List of errors on the custom field -->
      <div
        v-for="(error, index) in allErrors"
        :key="index"
        class="error flex flex-align-center">
        <SBIcon
          :icon="SBIconList.ERROR"
          :color="SBColors.redPrimary"
          :size="10"
          class="mr-1" />
        <span>{{ error }}</span>
      </div>
    </ValidationObserver>
  </div>
</template>

<script>
import { DatePicker } from 'element-ui';
import Multiselect from 'vue-multiselect';
import { extend } from 'vee-validate';
import { email } from 'vee-validate/dist/rules';

import SBColors from '@/helpers/lists/colors';
import SBIcon from '@/components/common/Icon';
import SBIconList from '@/helpers/lists/sb-icon-list';
import getFormat from '@/helpers/lists/countryTimeFormats';
import { mapGetters } from 'vuex';
import ToasterMixin from '@/mixins/Toaster';

extend('email', email);

function resetHeight(el) {
  el.style.height = 0;
  el.style.height = el.scrollHeight < 16 ? '16px' : `${el.scrollHeight}px`;
}

export default {
  name: 'CustomField',
  components: {
    elDatePicker: DatePicker,
    Multiselect,
    SBIcon,
  },
  directives: {
    autoheight: {
      // TODO create global directive
      bind: (el) => {
        resetHeight(el);
      },
      update: (el) => {
        resetHeight(el);
      },
    },
  },
  mixins: [
    ToasterMixin,
  ],
  props: {
    value: {
      type: undefined,
      default: null,
    },
    fieldp: {
      type: Object,
      default: () => {},
    },
  },
  data() {
    return {
      modelValue: null,
      preventEmit: false,
      // Needed to trick vue to use ref element for the computed variable
      isMounted: false,
      radioClicked: false,
      booleanOptions: [{ text: '', value: null }, { text: 'Yes', value: true }, { text: 'No', value: false }],
      SBIconList,
      SBColors,
    };
  },
  computed: {
    ...mapGetters({
      userRegion: 'user/region',
    }),
    /** Extracts all errors from validation observer and returns it as a list. */
    allErrors() {
      const errors = [];
      if (this.isMounted && this.$refs.customFieldObserver && this.$refs.customFieldObserver.errors) {
        Object.keys(this.$refs.customFieldObserver.errors).forEach((key) => {
          this.$refs.customFieldObserver.errors[key].forEach(error => errors.push(error));
        });
      }
      return errors;
    },
    hasPermissionToEdit() {
      return this.$hasPermissionInOrganisation('can_edit_contact');
    },
    options() {
      return [
        { name: '', value: null },
        ...this.fieldp.options,
      ];
    },
    dateFormat() {
      return getFormat(this.userRegion).el_date_picker_format;
    },
  },
  watch: {
    value(newValue) {
      this.preventEmit = true;
      if (this.fieldp.type === 'date') {
        this.modelValue = this.getDate(newValue);
      } else if (this.fieldp.type === 'list' && !this.isObject(newValue)) {
        this.modelValue = this.fieldp.options.find(item => item.value === this.value);
      } else if (this.fieldp.type === 'boolean') {
        this.modelValue = this.value !== null ?
          (this.value ? this.booleanOptions.find(item => item.value === true) : this.booleanOptions.find(item => item.value === false)) :
          null;
      } else {
        this.modelValue = newValue;
      }
      this.$nextTick(() => {
        this.preventEmit = false;
      });
    },
    modelValue() {
      if (!this.preventEmit) {
        this.$refs.customFieldObserver.validate();
        this.emitInput();
      }
    },
  },
  mounted() {
    this.preventEmit = true;
    if (this.fieldp.type === 'date') {
      this.modelValue = this.getDate(this.value);
    } else if (this.fieldp.type === 'list') {
      this.modelValue = this.fieldp.options.find(item => item.value === this.value);
    } else if (this.fieldp.type === 'boolean') {
      this.modelValue = this.value !== null ? (this.value ? this.booleanOptions.find(item => item.value === true) : this.booleanOptions.find(item => item.value === false)) : null;
    } else {
      this.modelValue = this.value;
    }
    this.isMounted = true;
    this.$nextTick(() => {
      this.preventEmit = false;
    });
  },
  methods: {
    deleteDate() {
      if (this.hasPermissionToEdit) {
        this.modelValue = 'null';
      }
    },
    emitInput() {
      setTimeout(() => {
        if (this.$refs.customFieldObserver.flags.valid) {
          if (this.fieldp.type === 'date') {
            this.$emit('input', this.$moment(this.modelValue).unix());
          } else if (this.fieldp.type === 'list' && this.modelValue && this.modelValue.value === null) {
            this.modelValue = undefined;
          } else if (this.fieldp.type === 'boolean') {
            if (this.modelValue.value === null) {
              this.modelValue = undefined;
              this.$emit('input', null);
            } else if (this.modelValue.value === true) {
              this.$emit('input', 1);
            } else {
              this.$emit('input', 0);
            }
          } else {
            this.$emit('input', this.modelValue);
          }
        }
      }, 0);
    },
    getDate(newValue) {
      if (newValue) {
        return this.$moment(false, false).unix(newValue);
      }
      return null;
    },
    decreaseNumber() {
      if (this.hasPermissionToEdit) {
        this.modelValue--;
      }
    },
    increaseNumber() {
      if (this.hasPermissionToEdit) {
        this.modelValue++;
      }
    },
    /**
     * HACK for radio buttons!!!
     * ==================================
     * Bootstrap-Vue does not support unchecking the radio if you click on the checed radio and we need that functionality.
     * This is why this hack is here and it exploits the fact that native click if being fired first and only after it finishes, the change event is fired.
     * And change event is fired only if there is a change on the model. This is how we can check if the already checked radio is being clicked againbundleRenderer.renderToStream
     * @param radioValue radioValue is set to a value if it comes from the change event and it tels us the value of the radio element. Native callback sets this value to null and it tells us to handle this call like native click which is fired before change happens.
     * @param event Click event from which we use to filter out the src element we will use for this action. Actually, this check only prevents the code to be run multiple times.
     */
    radioChange(radioValue, event) {
      if (radioValue === null && event && event.srcElement.classList.contains('custom-control-label')) { // run on native click call
        this.radioClicked = true;
        // timeout will be run at the end of a cycle when we can check if the radioClicked variable has been reset by change event
        setTimeout(() => {
          if (this.radioClicked) {
            this.radioClicked = false;
            this.modelValue = null;
          }
        }, 0);
      } else if (radioValue !== null) { // run on change event
        this.radioClicked = false;
      }
    },
    checkPermission() {
      if (!this.hasPermissionToEdit) {
        this.showToaster('You don’t have a permission to do this action.', null, null, 'warning');
      }
    },
  },
};
</script>

<style lang="scss" scoped>
.user-details-container {
  .validation-error {
    padding: 5px 0;
  }
}

.user-input::v-deep {
  font-size: 14px;
  padding: 0;
  position: relative;

  label {
    margin-top: 0;
  }

  textarea, input, select {
    color: $blackPrimary !important;
    resize: none;
    overflow: hidden;
    width: 100%;
    border: none;
    padding: 0;
    outline: none;
    background-color: unset !important;
    font-size: 14px;
    line-height: 1.2;
    color: $blackPrimary;
    box-shadow: none !important;

    &:active, &:focus {
      outline: none !important;
      box-shadow: none !important;
    }
  }

  select.edit-field {
    appearance: none;
    -webkit-appearance: none;
    -moz-appearance: none;

    &::-ms-expand {
      display: none;
    }

    &.not-selected {
      color: $cadetBlue !important;
    }
  }

  textarea, input, select {
    &.edit-field {
      cursor: text;
    }

    &.has-error {
      border-color: $redPrimary;
    }
  }

  // HIDE native spinners on number element
  /* Chrome, Safari, Edge, Opera */
  input::-webkit-outer-spin-button,
  input::-webkit-inner-spin-button {
    -webkit-appearance: none;
    margin: 0;
  }

  /* Firefox */
  input[type=number] {
    -moz-appearance: textfield;
  }
  // HIDE end

  .custom-radio {
    // cursor: pointer;

    .custom-control-label {
      line-height: 2;
      font-size: 12px;
      color: $cadetBlue;
    }
  }

  .number-input {
    min-height: 17px;
    padding: 0;

    .sb-icon {
      cursor: pointer;
    }
  }

  .el-date-editor {
    width: 100%;

    .el-input__prefix {
      left: 0;
    }

    .el-input__prefix, .el-input__suffix {
      .el-input__icon {
        color: $blackPrimary;
        width: 14px;
        line-height: 26px;
      }
    }

    .el-input__inner {
      display: block;
      width: 100%;
      height: 17px;
      max-width: 100%;
      padding: 3px 10px 3px 20px;
      box-sizing: border-box;
      line-height: 1.3;
      font-size: 14px;
      font-weight: 400;
      color: $blackPrimary;
      border: 1px solid transparent;
      border-radius: 5px;
      outline: none;
      cursor: pointer;
      background-color: unset !important;
    }
  }

  .clear-date {
    position: absolute;
    top: 0;
    right: 0;
    z-index: 1;
  }

  .error {
    margin: 4px 0 0 0;
    font-size: 12px;
    line-height: 1.4;
    color: #ff5b61;

    span:first-letter {
      text-transform: uppercase;
    }
  }
}

::v-deep {
  .multiselect__tags {
    justify-content: center;

    input {
      top: -1px !important;
    }
  }
}
</style>
